/* GoLiveBatchCard — one-click "clear DocuSign's 20-envelope threshold" tool.
 *
 * DocuSign requires you to log 20+ successful production-quality API calls
 * in demo before they'll promote your integration to production. This card
 * lets an admin fire off up to 19 self-addressed test envelopes in one
 * button click, sign them all on their phone, and be ready to hit "Go
 * Live" in the DocuSign Admin Console.
 *
 * The card only renders (from Contracts.jsx) when DocuSign JWT is
 * configured (i.e. real envelopes will be sent) and admin scope is
 * enforced on the backend endpoint.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { Rocket, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function GoLiveBatchCard() {
  const [count, setCount] = useState(19);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed && !result) return null;

  const send = async () => {
    if (!email.trim() || !name.trim()) {
      toast.error("Fill in your name and email first");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/docusign/go-live-batch", {
        count: Number(count) || 19,
        email: email.trim().toLowerCase(),
        name: name.trim(),
      });
      setResult(r.data);
      if (r.data.failed === 0) {
        toast.success(`Fired ${r.data.sent} envelopes — check your inbox`);
      } else {
        toast.warning(`Sent ${r.data.sent}, ${r.data.failed} failed — see details`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Batch send failed";
      toast.error(detail);
      setResult({ error: detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="go-live-batch-card"
      className="mb-4 rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-5 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-indigo-600 text-white p-2 shrink-0">
          <Rocket size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">
                DocuSign · Go-Live prep
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">
                Clear your 20-envelope threshold in one click.
              </h3>
            </div>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="text-xs text-slate-400 hover:text-slate-600"
              data-testid="golive-dismiss-btn"
            >
              Hide
            </button>
          </div>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            DocuSign only lets you promote your integration to production once you&apos;ve
            logged 20+ successful envelopes in demo. This tool fires N self-addressed
            test envelopes in one shot — you sign each on your phone, then submit
            <b> Admin Console → Apps and Keys → Go Live</b>.
          </p>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Your name</label>
              <input
                data-testid="golive-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tracy Smith"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Your email</label>
              <input
                data-testid="golive-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@ispeedtoclose.com"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">How many?</label>
              <input
                data-testid="golive-count-input"
                type="number"
                min={1}
                max={30}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              onClick={send}
              disabled={busy}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="golive-send-btn"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Rocket className="mr-2 h-4 w-4" /> Send {count || 19} test envelopes
                </>
              )}
            </Button>
            <a
              href="https://admindemo.docusign.com/apps-and-keys"
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-indigo-700 underline"
            >
              Open DocuSign Admin (demo) →
            </a>
          </div>

          {result && !result.error && (
            <div
              data-testid="golive-result"
              className="mt-4 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900"
            >
              <div className="flex items-center gap-2 font-bold">
                <CheckCircle2 size={16} />
                Sent {result.sent} · Failed {result.failed}
              </div>
              <p className="mt-1 text-emerald-800/90">{result.hint}</p>
              {result.envelopes?.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer font-semibold">Envelope IDs</summary>
                  <ul className="mt-1 font-mono">
                    {result.envelopes.map((e) => (
                      <li key={e.envelope_id}>#{e.seq} · {e.envelope_id}</li>
                    ))}
                  </ul>
                </details>
              )}
              {result.errors?.length > 0 && (
                <details open className="mt-2 text-xs">
                  <summary className="cursor-pointer font-semibold text-rose-800">
                    ⚠️ {result.errors.length} envelope error{result.errors.length === 1 ? "" : "s"} — click to expand
                  </summary>
                  <ul className="mt-2 space-y-1 text-rose-900 bg-rose-50 border border-rose-200 rounded p-2 font-mono max-h-64 overflow-auto">
                    {result.errors.map((e, i) => (
                      <li key={i} className="break-all">
                        <b>#{e.seq}:</b> {e.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {result?.error && (
            <div
              data-testid="golive-error"
              className="mt-4 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 flex items-start gap-2"
            >
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>{result.error}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
